import {TranslationService} from '@emma-labs/shared/angular';
import {
  ArchivedProposal,
  ArchivedUserInfo,
  calculateAgeTruncated,
  CategoryCode,
  CustomRevision,
  ExtraRequirementsFile,
  Fna,
  getCodeFromLanguage,
  getDerivedUnderwritingProfileStatus,
  getLanguageFromCode,
  HealthAnswers,
  HealthAnswersRevision,
  HealthAnswersRevisionType,
  HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS,
  PresetRevision,
  Proposal,
  PublicProfile,
  QuestionId,
  QuestionnaireId,
  QuestionnaireStatus,
  Sex,
  TranslatedUiQuestion,
  UnderwritingProfileStatus,
  UserInfo,
} from '@emma-labs/shared/typescript';
import {ExtraQuestionnaire as ArchivedExtraQuestionnaire} from '../entities/archived-proposal-object/archived-proposal-page-object';
import {Client} from '../entities/proposal-page-object/client';
import {
  ExtraQuestionnaire,
  ProposalPageObject,
  SectionCompleted,
} from '../entities/proposal-page-object/proposal-page-object';
import {ClientInfo} from '../entities/underwriting-page-object/underwriting-page-object';
import {
  EXTRA_QUESTIONNAIRE_DETAILS_EN_TEMPLATE,
  EXTRA_QUESTIONNAIRE_DETAILS_FR_TEMPLATE,
} from '../ui/proposal-page/extra-questionnaires-section/details-message-templates';

export function buildProposalPageObject(
  isFr: boolean,
  newParams: {
    proposal?: Proposal;
    fna?: Fna;
    healthQuestionnaire?: {
      questions: TranslatedUiQuestion[];
      status: QuestionnaireStatus;
      categoryCode?: CategoryCode;
      extraQuestionnaires?: ExtraQuestionnaire[];
    };
    primaryAgent?: PublicProfile;
  },
  previousProposalPageObject: ProposalPageObject = {
    sectionCompleted: {} as {[K in keyof SectionCompleted]: false},
  },
): ProposalPageObject {
  const newProposalPageObject: ProposalPageObject = JSON.parse(JSON.stringify(previousProposalPageObject));

  if (newParams.proposal) {
    const proposal = newParams.proposal;
    const submitter = proposal.submitter;
    const healthAnswers = proposal.healthAnswers;

    newProposalPageObject.proposalId = proposal.id;
    newProposalPageObject.client = {
      firstName: submitter?.firstName,
      middleName: submitter?.middleName,
      lastName: submitter?.lastName,
      province: submitter?.province,
      phone: submitter?.phone,
      email: submitter?.email,
      preferredLanguage: submitter?.languageCode ? getLanguageFromCode(submitter.languageCode) : undefined,
      birthday: healthAnswers?.birthday ? new Date(healthAnswers?.birthday) : undefined,
      sex: healthAnswers?.sex,
    };
    newProposalPageObject.sectionCompleted.client = true;

    newProposalPageObject.healthAnswers = healthAnswers;

    newProposalPageObject.isProtectionReset = !!previousProposalPageObject.protection && !proposal.protection;
    newProposalPageObject.protection = proposal.protection;
    newProposalPageObject.sectionCompleted.protectionBuilder = !!proposal.protection;

    newProposalPageObject.beneficiaries = proposal.beneficiaries
      ? proposal.beneficiaries.map((b) => ({
          ...b,
          birthday: b.birthday ? new Date(b.birthday) : undefined,
          trustee: b.trustee
            ? {
                ...b.trustee,
                birthday: b.trustee?.birthday ? new Date(b.trustee.birthday) : undefined,
              }
            : undefined,
        }))
      : [];
    newProposalPageObject.sectionCompleted.beneficiaries = true;

    newProposalPageObject.replacementNotice = {
      hasPolicyReplacementNotice: proposal.hasPolicyReplacementNotice,
    };
    newProposalPageObject.age = healthAnswers?.birthday
      ? calculateAgeTruncated(new Date(healthAnswers?.birthday), proposal.submittedAt)
      : undefined;
    if (proposal.jobCategoryId) {
      newProposalPageObject.jobRiskClass = HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS[proposal.jobCategoryId];
    }
    newProposalPageObject.sectionCompleted.replacementNotice = proposal.hasPolicyReplacementNotice != null;

    newProposalPageObject.address = {
      address: submitter?.address,
      city: submitter?.city,
      postalCode: submitter?.postalCode,
      unitNumber: submitter?.unitNumber,
    };
    newProposalPageObject.sectionCompleted.address =
      !!submitter?.address && !!submitter.city && !!submitter.postalCode;

    newProposalPageObject.idDocument = proposal.extra?.idDocument;
    newProposalPageObject.sectionCompleted.idDocument = !!proposal.extra?.idDocument;

    newProposalPageObject.disclosures = proposal.extra?.clientAgentAffiliationTime
      ? {
          clientAgentAffiliationTime: proposal.extra.clientAgentAffiliationTime,
          isAccepted: proposal.extra.isAgentDisclosuresAccepted,
        }
      : undefined;
    newProposalPageObject.sectionCompleted.disclosures =
      !!proposal.extra?.clientAgentAffiliationTime && proposal.extra.isAgentDisclosuresAccepted === true;

    newProposalPageObject.isSent = !!proposal.extra?.sentAt;

    newProposalPageObject.ratesVersion = proposal.providerVersion.ratesVersion;

    newProposalPageObject.willRequireExtraRequirements = proposal.willRequireExtraRequirements;

    newProposalPageObject.note = proposal.extra?.note;
  }

  if (newParams.fna) {
    newProposalPageObject.fna = newParams.fna;
    newProposalPageObject.fna.jobCategoryId = newParams.proposal?.jobCategoryId;
    if (newParams.fna.isComplete != null) {
      newProposalPageObject.sectionCompleted.fna = newParams.fna.isComplete;
    }
  }

  if (newParams.healthQuestionnaire) {
    newProposalPageObject.healthQuestionnaire = {
      status: newParams.healthQuestionnaire.status,
      categoryCode: newParams.healthQuestionnaire.categoryCode,
      questions: newParams.healthQuestionnaire.questions.filter(
        (q) => q.id !== QuestionId.SEX && q.id !== QuestionId.BIRTHDAY,
      ),
    };
    newProposalPageObject.sectionCompleted.healthQuestionnaire =
      newParams.healthQuestionnaire.status !== QuestionnaireStatus.INCOMPLETE;
    newProposalPageObject.extraQuestionnaires = buildExtraQuestionnairesWithTemplate(
      isFr,
      newParams.healthQuestionnaire.extraQuestionnaires,
    );

    newProposalPageObject.sectionCompleted.extraQuestionnaires =
      newParams.healthQuestionnaire.extraQuestionnaires?.every(
        (eq) => eq.status === QuestionnaireStatus.COMPLETE,
      ) ?? true;
  }

  if (newParams.primaryAgent) {
    newProposalPageObject.primaryAgentFullName = `${newParams.primaryAgent.firstName} ${newParams.primaryAgent.lastName}`;
  }

  return newProposalPageObject;
}

export function getSubmitterFromClient(client: Client): UserInfo {
  return {
    firstName: client.firstName,
    middleName: client.middleName,
    lastName: client.lastName,
    province: client.province,
    phone: client.phone,
    email: client.email,
    languageCode: client.preferredLanguage ? getCodeFromLanguage(client.preferredLanguage!) : undefined,
  };
}

export function getHealthAnswersFromClient(client: Client): HealthAnswers {
  return {
    sex: client.sex,
    birthday: client.birthday,
  };
}

export function combineExtraQuestionnaires(
  isFr: boolean,
  archivedProposal: ArchivedProposal,
  haExtraQuestionnaires?: {
    id: QuestionnaireId;
    title: string;
    questions: TranslatedUiQuestion[];
    status: QuestionnaireStatus;
  }[],
  revisionsExtraQuestionnaires?: {
    id: QuestionnaireId;
    title: string;
    questions: TranslatedUiQuestion[];
    status: QuestionnaireStatus;
  }[],
): ArchivedExtraQuestionnaire[] {
  const extraQuestionnaires: ArchivedExtraQuestionnaire[] = [];
  if (haExtraQuestionnaires) {
    extraQuestionnaires.push(
      ...haExtraQuestionnaires
        .filter((eq) => eq.status === QuestionnaireStatus.COMPLETE)
        .map((eq) => ({
          id: eq.id,
          title: eq.title,
          presetQuestions: eq.questions,
          type: HealthAnswersRevisionType.PRESET,
          completedAt: archivedProposal.saleReport.createdAt,
          isIncludedInContract: true,
        })),
    );
  }

  if (archivedProposal.healthAnswersRevisions && revisionsExtraQuestionnaires) {
    extraQuestionnaires.push(
      ...revisionsExtraQuestionnaires.map((eq) => {
        const associatedRevision = archivedProposal.healthAnswersRevisions!.find(
          (r) =>
            r.type === HealthAnswersRevisionType.PRESET &&
            (r.revision as PresetRevision).questionnaires.map((q) => q.questionnaireId).includes(eq.id),
        )!;
        return {
          id: eq.id,
          title: eq.title,
          presetQuestions: eq.questions,
          type: associatedRevision.type,
          revisionId: associatedRevision.id,
          completedAt: associatedRevision.completedAt,
          isIncludedInContract: associatedRevision.isIncludedInContract,
        };
      }),
      ...archivedProposal.healthAnswersRevisions
        .filter((har) => har.type === HealthAnswersRevisionType.CUSTOM)
        .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime())
        .map((har, index) => ({
          title: isFr
            ? `Question supplémentaire personnalisée ${index + 1}`
            : `Custom additional question ${index + 1}`,
          type: har.type,
          revisionId: har.id,
          customQuestion: {
            text: (har.revision as CustomRevision).question,
            answer: (har.revision as CustomRevision).answer,
            documentNames: (har.revision as CustomRevision).documentNames,
          },
          completedAt: har.completedAt,
          isIncludedInContract: har.isIncludedInContract,
        })),
    );
  }

  return extraQuestionnaires;
}

export function buildExtraQuestionnairesWithTemplate(
  isFr: boolean,
  extraQuestionnaires: ExtraQuestionnaire[] | undefined,
): ExtraQuestionnaire[] | undefined {
  if (!extraQuestionnaires) {
    return undefined;
  }

  const eqs = JSON.parse(JSON.stringify(extraQuestionnaires));

  eqs.forEach((eq) => {
    if (eq.id === QuestionnaireId.DETAILS) {
      eq.questions?.forEach((q) => {
        if (!q.answer.value) {
          q.answer.value = isFr
            ? EXTRA_QUESTIONNAIRE_DETAILS_FR_TEMPLATE
            : EXTRA_QUESTIONNAIRE_DETAILS_EN_TEMPLATE;
        }
      });
    }
  });
  return eqs;
}

export function buildStatusObject(
  translate: TranslationService,
  status: UnderwritingProfileStatus,
  healthAnswersRevisions?: HealthAnswersRevision[],
  extraRequirementsFile?: ExtraRequirementsFile,
) {
  const derivedStatus = getDerivedUnderwritingProfileStatus(
    status,
    healthAnswersRevisions,
    extraRequirementsFile,
  );

  return {
    value: derivedStatus,
    text: translate.getTranslation(derivedStatus)!,
  };
}

export function buildClientInfoObject(
  insuree: ArchivedUserInfo,
  birthday: Date,
  sex: Sex,
  proposalSubmittedAt: Date,
  hasPolicyReplacementNotice: boolean,
  salary?: number,
): ClientInfo {
  if (!insuree.birthCountry) {
    throw new Error('There should be a birth country defined for a client in underwriting');
  }

  return {
    firstName: insuree.firstName,
    middleName: insuree.middleName,
    lastName: insuree.lastName,
    birthday: new Date(birthday),
    age: calculateAgeTruncated(birthday, proposalSubmittedAt),
    sex,
    preferredLanguage: getLanguageFromCode(insuree.languageCode),
    email: insuree.email,
    phone: insuree.phone,
    province: insuree.province,
    birthCountry: insuree.birthCountry,
    birthState: insuree.birthState,
    address: insuree.address,
    unitNumber: insuree.unitNumber,
    city: insuree.city,
    postalCode: insuree.postalCode,
    salary,
    hasPolicyReplacementNotice,
  };
}
